import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    TextField, Box, FormControl, InputLabel, FormHelperText, Button, Select,
    MenuItem, Alert
} from '@mui/material';
import LangSelect from '@components/LangSelect';
import { getGenderName } from '@utils/helpers';

const ContactForm = forwardRef(({ contact, onSubmit, fieldsVariant = "standard", showInfoMessage = false }, ref) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);

    const validationSchema = Yup.object({
        first_name: Yup.string()
            .required(t('This field is required')),
        last_name: Yup.string()
            .required(t('This field is required')),
        email: Yup.string()
            .email(t('Invalid email format'))
            .nullable(),
        phone: Yup.string()
            .nullable(),
        gender: Yup.string()
            .nullable(),
        lang: Yup.string()
            .nullable()
    });

    const { control, handleSubmit, setValue, setError, formState: { errors, isValid } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onTouched',
        reValidateMode: 'onBlur',
    });

    useImperativeHandle(ref, () => ({ setError }));

    useEffect(() => {
        if (contact) {
            for (const [key, value] of Object.entries(contact)) {
                setValue(key, value);
            }
        }
        setLoaded(true);
    }, [contact, setValue]);

    if (!loaded) return null;

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box mb={3}>
                <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            error={Boolean(errors.gender)}
                        >
                            <InputLabel>{t('Gender')}</InputLabel>
                            <Select {...field}>
                                <MenuItem value="">{t('- No selection -')}</MenuItem>
                                <MenuItem value="m">{getGenderName(t, 'm')}</MenuItem>
                                <MenuItem value="f">{getGenderName(t, 'f')}</MenuItem>
                                <MenuItem value="o">{getGenderName(t, 'o')}</MenuItem>
                            </Select>
                            {errors.gender && <FormHelperText>{errors.gender.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label={t('First name')}
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.first_name)}
                            helperText={errors.first_name?.message}
                        />
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label={t('Last name')}
                            variant={fieldsVariant}
                            required
                            error={Boolean(errors.last_name)}
                            helperText={errors.last_name?.message}
                        />
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label={t('Email')}
                            variant={fieldsVariant}
                            error={Boolean(errors.email)}
                            helperText={errors.email?.message}
                        />
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            fullWidth
                            label={t('Phone')}
                            variant={fieldsVariant}
                            error={Boolean(errors.phone)}
                            helperText={t('International format (e.g., +33612345678)')}
                        />
                    )}
                />
            </Box>

            <Box mb={3}>
                <Controller
                    name="lang"
                    control={control}
                    render={({ field }) => (
                        <FormControl
                            fullWidth
                            variant={fieldsVariant}
                            error={Boolean(errors.lang)}
                        >
                            <InputLabel>{t('Language')}</InputLabel>
                            <LangSelect
                                {...field}
                                translatedInCurrentLanguage={true}
                                emptyLabel={t('- No selection -')}
                            />
                            {errors.lang && <FormHelperText>{errors.lang.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
            </Box>

            {showInfoMessage && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('Remarque : if a sendout exists on this mailing list, this contact will not be taken into account. It will only be processed for future sendouts.')}
                </Alert>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                >
                    {t('Validate')}
                </Button>
            </Box>
        </form>
    );
});

export default ContactForm; 